import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

// Types
export interface VocabularyEntry {
  id: number;
  word: string;
  lessonId: number;
  lesson?: {
    id: number;
    date: string;
    topic?: string;
    group: {
      id: number;
      name?: string;
      level: string;
    };
  };
}

export interface VocabularyGroup {
  id: number;
  name?: string;
  level: string;
  displayName: string;
}

export interface VocabularyState {
  entries: VocabularyEntry[];
  groups: VocabularyGroup[];
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  filters: {
    search: string;
    groupId?: number;
    level?: string;
    dateFrom?: string;
    dateTo?: string;
  };
  stats: {
    totalWords: number;
    totalLessons: number;
    totalGroups: number;
  };
}

// Initial state
const initialState: VocabularyState = {
  entries: [],
  groups: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
  },
  filters: {
    search: '',
    groupId: undefined,
    level: undefined,
    dateFrom: undefined,
    dateTo: undefined,
  },
  stats: {
    totalWords: 0,
    totalLessons: 0,
    totalGroups: 0,
  },
};

// API Base URL
const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// Helper function to get auth headers
const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Async thunks for fetching vocabulary data from lessons
export const fetchVocabularyAsync = createAsyncThunk(
  'vocabulary/fetchVocabulary',
  async (params: { page?: number; limit?: number; search?: string; groupId?: number; level?: string; dateFrom?: string; dateTo?: string } = {}, { rejectWithValue, getState }) => {
    try {
      const { page = 1, limit = 50, search = '', groupId, level, dateFrom, dateTo } = params;
      
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      
      if (search) queryParams.append('search', search);
      if (groupId) queryParams.append('groupId', groupId.toString());
      if (level) queryParams.append('level', level);
      if (dateFrom) queryParams.append('dateFrom', dateFrom);
      if (dateTo) queryParams.append('dateTo', dateTo);

      // This would be a custom endpoint to get all vocabulary with pagination
      const response = await axios.get(`${API_BASE_URL}/vocabulary?${queryParams}`, {
        headers: getAuthHeaders(getState)
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch vocabulary'));
    }
  }
);

export const fetchPublicVocabularyAsync = createAsyncThunk(
  'vocabulary/fetchPublicVocabulary',
  async (groupId: number, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/vocabulary?group=${groupId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch public vocabulary'));
    }
  }
);

export const fetchPublicGroupsAsync = createAsyncThunk(
  'vocabulary/fetchPublicGroups',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/groups`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch groups'));
    }
  }
);

export const fetchVocabularyStatsAsync = createAsyncThunk(
  'vocabulary/fetchVocabularyStats',
  async (_, { rejectWithValue, getState }) => {
    try {
      // This would be a custom endpoint to get vocabulary statistics
      const response = await axios.get(`${API_BASE_URL}/vocabulary/stats`, {
        headers: getAuthHeaders(getState)
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch vocabulary stats'));
    }
  }
);

const vocabularySlice = createSlice({
  name: 'vocabulary',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<Partial<VocabularyState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
      state.pagination.currentPage = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch vocabulary
      .addCase(fetchVocabularyAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVocabularyAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.entries = action.payload.vocabulary || [];
        state.pagination = action.payload.pagination || state.pagination;
        state.error = null;
      })
      .addCase(fetchVocabularyAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch public vocabulary
      .addCase(fetchPublicVocabularyAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPublicVocabularyAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Transform lessons data to vocabulary entries format
        const entries: VocabularyEntry[] = [];
        const lessons = action.payload.lessons as
          | Array<{
              id: number;
              date: string;
              topic?: string;
              vocabulary?: Array<{ id: number; word: string }>;
            }>
          | undefined;
        lessons?.forEach((lesson) => {
          lesson.vocabulary?.forEach((vocab) => {
            entries.push({
              id: vocab.id,
              word: vocab.word,
              lessonId: lesson.id,
              lesson: {
                id: lesson.id,
                date: lesson.date,
                topic: lesson.topic,
                group: action.payload.group
              }
            });
          });
        });
        state.entries = entries;
        state.stats = {
          totalWords: action.payload.totalWords || 0,
          totalLessons: action.payload.totalLessons || 0,
          totalGroups: 1
        };
        state.error = null;
      })
      .addCase(fetchPublicVocabularyAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch public groups
      .addCase(fetchPublicGroupsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPublicGroupsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload.groups || [];
        state.error = null;
      })
      .addCase(fetchPublicGroupsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch vocabulary stats
      .addCase(fetchVocabularyStatsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVocabularyStatsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload;
        state.error = null;
      })
      .addCase(fetchVocabularyStatsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setFilters, clearFilters, setPage } = vocabularySlice.actions;
export default vocabularySlice.reducer; 